import axios from 'axios';
import { transliterate, fuzzyNormalize } from '../../utils/translit';
import { normalize, isArtistMatch, isValidArtwork } from '../../utils/stringCleaners';

/**
 * Deezer API Provider
 * Proxied via public/deezer_proxy.php to avoid CORS
 */

const SEARCH_TIMEOUT = 20000;

/**
 * Search Deezer for track artwork
 * @param {string} queryArtist - Artist name to search
 * @param {string} queryTrack - Track name to search
 * @param {string} validateArtist - Artist name for validation
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const searchDeezerTrack = async (queryArtist, queryTrack, validateArtist) => {
    try {
        const term = `${queryArtist} ${queryTrack}`.trim();
        console.log(`[Deezer] Searching: "${term}"`);

        const response = await axios.get(`/deezer_proxy.php`, {
            params: { q: term },
            timeout: SEARCH_TIMEOUT
        });

        if (response.data && response.data.data && response.data.data.length > 0) {
            const results = response.data.data;
            const targetTrackNorm = normalize(queryTrack);
            const isTargetLive = targetTrackNorm.includes('live');

            for (const item of results) {
                if (isArtistMatch(item.artist.name, validateArtist)) {
                    const itemTrackNorm = normalize(item.title);
                    const isItemLive = itemTrackNorm.includes('live');

                    if (isTargetLive === isItemLive) {
                        const fuzzyItemTrack = fuzzyNormalize(itemTrackNorm);
                        const fuzzyTargetTrack = fuzzyNormalize(targetTrackNorm);

                        if (itemTrackNorm.includes(targetTrackNorm) || targetTrackNorm.includes(itemTrackNorm) ||
                            fuzzyItemTrack.includes(fuzzyTargetTrack) || fuzzyTargetTrack.includes(fuzzyItemTrack)) {

                            if (item.album && item.album.cover_xl && isValidArtwork(item.album.cover_xl)) {
                                console.log(`[Deezer] Found: ${item.album.cover_xl}`);
                                return item.album.cover_xl;
                            }
                        }
                    }
                }
            }
        }
    } catch (e) {
        console.warn('[Deezer] Error:', e);
    }
    return null;
};

/**
 * Search Deezer for artist photo (fallback)
 * @param {string} cleanArtist - Cleaned artist name
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const searchDeezerArtist = async (cleanArtist) => {
    try {
        console.log(`[Deezer] Searching artist: "${cleanArtist}"`);

        const response = await axios.get(`/deezer_proxy.php`, {
            params: { q: cleanArtist, type: 'artist' },
            timeout: SEARCH_TIMEOUT
        });

        if (response.data && response.data.data && response.data.data.length > 0) {
            const artistObj = response.data.data[0];

            // Strict name match
            if (normalize(artistObj.name) === normalize(cleanArtist)) {
                if (artistObj.picture_xl && isValidArtwork(artistObj.picture_xl)) {
                    console.log(`[Deezer] Found artist: ${artistObj.picture_xl}`);
                    return artistObj.picture_xl;
                }
            }
        }
    } catch (e) {
        console.warn('[Deezer] Error:', e);
    }

    // Fallback search with transliterated name
    const transArtist = transliterate(cleanArtist);
    if (transArtist !== cleanArtist) {
        console.log(`[Deezer] Retrying artist search with transliteration: "${transArtist}"`);
        return searchDeezerArtist(transArtist);
    }

    return null;
};
